import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Mail, 
  Bug, 
  MessageCircle, 
  ExternalLink,
  Headphones,
  FileText
} from 'lucide-react';
import axios from 'axios';

import { API_BASE_URL } from '../utils/api';
const API = API_BASE_URL;

export default function Support() {
  const { token } = useAuth();
  const [supportConfig, setSupportConfig] = useState({
    support_email: 'support@connect-us.co.nz',
    bug_report_email: 'bugs@connect-us.co.nz'
  });

  useEffect(() => {
    const fetchSupportConfig = async () => {
      try {
        const res = await axios.get(`${API}/api/settings/support`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data) {
          setSupportConfig(res.data);
        }
      } catch (error) {
        // Use defaults if not configured
        console.log('Using default support emails');
      }
    };
    
    if (token) {
      fetchSupportConfig();
    }
  }, [token]);

  return (
    <div className="p-8 max-w-3xl mx-auto" data-testid="support-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-heading font-bold text-foreground">Support</h1>
        <p className="text-muted-foreground mt-1">Get help with CONNECT</p>
      </div>

      <div className="grid gap-6">
        {/* Contact Support Card */}
        <Card className="border-border/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Headphones className="w-5 h-5" />
              Contact Support
            </CardTitle>
            <CardDescription>
              Need assistance? Our support team is here to help.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/30 border border-border/50">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Mail className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">General Support</p>
                  <p className="text-sm text-muted-foreground">Questions, help, and guidance</p>
                </div>
              </div>
              <Button 
                variant="outline" 
                onClick={() => window.location.href = `mailto:${supportConfig.support_email}`}
                data-testid="support-email-btn"
              >
                <Mail className="w-4 h-4 mr-2" />
                {supportConfig.support_email}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Bug Report Card */}
        <Card className="border-border/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bug className="w-5 h-5" />
              Report a Bug
            </CardTitle>
            <CardDescription>
              Found something that's not working correctly? Let us know.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/30 border border-border/50">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-destructive/10">
                  <Bug className="w-5 h-5 text-destructive" />
                </div>
                <div>
                  <p className="font-medium">Bug Reports</p>
                  <p className="text-sm text-muted-foreground">Technical issues and errors</p>
                </div>
              </div>
              <Button 
                variant="outline" 
                onClick={() => window.location.href = `mailto:${supportConfig.bug_report_email}?subject=Bug Report: [Brief Description]`}
                data-testid="bug-report-email-btn"
              >
                <Mail className="w-4 h-4 mr-2" />
                {supportConfig.bug_report_email}
              </Button>
            </div>
            
            <div className="text-sm text-muted-foreground bg-secondary/20 p-4 rounded-lg">
              <p className="font-medium mb-2">When reporting a bug, please include:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Steps to reproduce the issue</li>
                <li>What you expected to happen</li>
                <li>What actually happened</li>
                <li>Your browser and device information</li>
                <li>Screenshots if applicable</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Documentation Card */}
        <Card className="border-border/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Documentation
            </CardTitle>
            <CardDescription>
              Learn more about using CONNECT
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 gap-4">
              <a 
                href="/app/help" 
                className="flex items-center gap-3 p-4 rounded-lg bg-secondary/30 border border-border/50 hover:bg-secondary/50 transition-colors"
              >
                <MessageCircle className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-medium">Help Center</p>
                  <p className="text-sm text-muted-foreground">FAQs and guides</p>
                </div>
              </a>
              <a 
                href="/app/about" 
                className="flex items-center gap-3 p-4 rounded-lg bg-secondary/30 border border-border/50 hover:bg-secondary/50 transition-colors"
              >
                <ExternalLink className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-medium">About CONNECT</p>
                  <p className="text-sm text-muted-foreground">Learn about our platform</p>
                </div>
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
